import React, { useState, useRef, useEffect } from 'react';
import { arNum } from '../utils/arFormat';

// Shared between PassengerSearchPage and OfficeNewBookingPage so the office
// portal's trip search looks and behaves identically to the passenger one —
// extracted rather than duplicated so a future tweak to either only has to
// happen once.

/* ═══════════════════════════════════════════════════════════
   CitySelect — native <select> with icon + custom chevron
═══════════════════════════════════════════════════════════ */
// Alef/ya/ta-marbuta variants and tashkeel are the main reason a plain
// substring search misses obvious matches in Arabic — most people type "ا"
// where a city name actually has "أ"/"إ"/"آ", or drop the hamza entirely.
// Folding both sides through the same normalizer fixes that without needing
// a real transliteration library for what's just a client-side filter.
const normalizeArabic = (s) =>
  String(s || '')
    .normalize('NFKD')
    .replace(/[ً-ٟ]/g, '')   // tashkeel/diacritics
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .trim();

export const CitySelect = ({ value, onChange, cities = [], loadingCities = false, icon }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch('');
      // Panel + input mount this same tick — wait a frame so focus() lands
      // on an element that's actually in the DOM.
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open]);

  const select = (city) => { onChange(city); setOpen(false); };

  const filteredCities = search.trim()
    ? cities.filter((c) => normalizeArabic(c).includes(normalizeArabic(search)))
    : cities;

  return (
    <div ref={wrapRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        disabled={loadingCities}
        onClick={() => setOpen(o => !o)}
        className={[
          'w-full flex items-center bg-white border-2 rounded-2xl',
          'py-3.5 pr-3.5 pl-9 text-sm transition-all cursor-pointer text-right',
          open ? 'border-[#1E3A8A] ring-4 ring-blue-100' : 'border-gray-200 hover:border-[#1E3A8A]/40',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        ].join(' ')}
      >
        {/* Left icon (location/flag) */}
        <span className="flex-shrink-0 ml-0 mr-0 flex items-center">{icon}</span>
        {/* Label */}
        <span className={`flex-1 mx-2 truncate ${value ? 'text-gray-900 font-semibold' : 'text-gray-400'}`}>
          {value || 'اختر المدينة'}
        </span>
        {/* Chevron */}
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none">
          <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
          </svg>
        </span>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute z-50 mt-1.5 w-full bg-white border border-gray-200 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,.12)] overflow-hidden">
          {/* Search box — replaces the old static "اختر المدينة" header */}
          <div className="p-2 bg-[#1E3A8A]">
            <div className="relative">
              <svg className="absolute inset-y-0 right-3 my-auto w-4 h-4 text-white/60 pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/>
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && filteredCities.length > 0) select(filteredCities[0]);
                  if (e.key === 'Escape') setOpen(false);
                }}
                placeholder="ابحث عن مدينة..."
                className="w-full rounded-xl border-0 bg-white/10 py-2 pr-9 pl-3 text-sm text-white placeholder-white/60 text-right focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto overscroll-contain py-1" role="listbox">
            {loadingCities ? (
              <li className="px-4 py-2.5 text-sm text-gray-400 text-right">جاري تحميل المدن...</li>
            ) : filteredCities.length === 0 ? (
              <li className="px-4 py-2.5 text-sm text-gray-400 text-right">
                {cities.length === 0 ? 'لا توجد مدن' : 'لا توجد نتائج مطابقة'}
              </li>
            ) : filteredCities.map(city => (
              <li
                key={city}
                role="option"
                aria-selected={city === value}
                onClick={() => select(city)}
                className={[
                  'px-4 py-2.5 text-sm text-right cursor-pointer transition-colors select-none',
                  city === value
                    ? 'bg-blue-50 text-[#1E3A8A] font-bold'
                    : 'text-gray-800 hover:bg-gray-50 font-medium',
                ].join(' ')}
              >
                {city}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   ArabicDatePicker — custom calendar, Arabic-Indic numerals
═══════════════════════════════════════════════════════════ */
const ARABIC_MONTHS = [
  'يناير','فبراير','مارس','أبريل','مايو','يونيو',
  'يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر',
];
const WEEK_HEADERS = ['أح','اث','ثل','أر','خم','جم','سب'];

// Roughly how tall the calendar panel renders (nav row + weekday header +
// up to 6 day rows + padding) — used to decide whether it fits below the
// trigger before opening downward blindly.
const CALENDAR_PANEL_HEIGHT = 340;

export const ArabicDatePicker = ({ value, onChange, min, placeholder, allowClear = false }) => {
  const todayStr = new Date().toISOString().split('T')[0];
  const [open,      setOpen]      = useState(false);
  // On a shorter laptop viewport the trigger can sit low enough that the
  // panel opening downward runs off the bottom of the page and needs a
  // scroll to see the later rows — flip it to open upward instead when
  // there isn't enough room below, same as native date pickers do.
  const [openUpward, setOpenUpward] = useState(false);
  const [viewYear,  setViewYear]  = useState(() => value ? +value.split('-')[0] : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? +value.split('-')[1] - 1 : new Date().getMonth());
  const wrapRef = useRef(null);

  useEffect(() => {
    if (value) { setViewYear(+value.split('-')[0]); setViewMonth(+value.split('-')[1] - 1); }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const toggleOpen = () => {
    if (!open && wrapRef.current) {
      const spaceBelow = window.innerHeight - wrapRef.current.getBoundingClientRect().bottom;
      setOpenUpward(spaceBelow < CALENDAR_PANEL_HEIGHT);
    }
    setOpen(o => !o);
  };

  const pad  = (n) => String(n).padStart(2, '0');
  const ds   = (d) => `${viewYear}-${pad(viewMonth + 1)}-${pad(d)}`;
  const isDisabled = (d) => !!min && ds(d) < min;
  const isSelected = (d) => value === ds(d);
  const isToday    = (d) => todayStr === ds(d);

  const daysInMonth  = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstWeekDay = new Date(viewYear, viewMonth, 1).getDay();
  const cells = [...Array(firstWeekDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear(y => y - 1)) : setViewMonth(m => m - 1);
  const nextMonth = () => viewMonth === 11 ? (setViewMonth(0), setViewYear(y => y + 1)) : setViewMonth(m => m + 1);
  const selectDay = (d) => { onChange(ds(d)); setOpen(false); };

  const displayValue = value
    ? (() => { const [y, m, d] = value.split('-'); return `${arNum(d)} / ${arNum(m)} / ${arNum(y)}`; })()
    : null;

  return (
    <div ref={wrapRef} className="relative">
      <div
        onClick={toggleOpen}
        className={`w-full border-2 rounded-2xl px-4 py-3.5 bg-white transition-all flex items-center gap-3 cursor-pointer select-none ${
          open ? 'border-[#1E3A8A] ring-4 ring-blue-100' : 'border-gray-200 hover:border-[#1E3A8A]/40'
        }`}
      >
        <span className="text-gray-500 text-sm flex-shrink-0">📅</span>
        <span className={`text-sm flex-1 whitespace-nowrap overflow-hidden ${displayValue ? 'text-gray-800 font-semibold' : 'text-gray-400'}`}>
          {displayValue || placeholder || 'اختر التاريخ'}
        </span>
        <svg className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
        </svg>
      </div>

      {open && (
        <div dir="rtl" className={`absolute z-[60] right-0 bg-white rounded-2xl shadow-[0_8px_32px_rgba(10,77,157,.20)] border border-gray-100 p-4 w-[272px] max-h-[80vh] overflow-y-auto ${
          openUpward ? 'bottom-full mb-2' : 'top-full mt-2'
        }`}>
          {/* Month/Year nav — in RTL: first child = right = prev, last child = left = next */}
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={prevMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
            <span className="text-sm font-black text-gray-800">
              {ARABIC_MONTHS[viewMonth]} {arNum(viewYear)}
            </span>
            <button type="button" onClick={nextMonth}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-600 transition-colors">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
          </div>

          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 mb-1">
            {WEEK_HEADERS.map(h => (
              <div key={h} className="text-center text-[10px] font-bold text-gray-400 py-1">{h}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((day, i) => (
              <div key={i} className="flex items-center justify-center">
                {day ? (
                  <button
                    type="button"
                    onClick={() => !isDisabled(day) && selectDay(day)}
                    className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all flex items-center justify-center
                      ${isSelected(day) ? 'bg-[#1E3A8A] text-white font-bold' :
                        isToday(day)    ? 'bg-blue-50 text-[#1E3A8A] font-bold border border-[#1E3A8A]/30' :
                        isDisabled(day) ? 'text-gray-200 cursor-not-allowed' :
                                          'text-gray-700 hover:bg-blue-50 hover:text-[#1E3A8A]'}`}
                  >
                    {arNum(day)}
                  </button>
                ) : null}
              </div>
            ))}
          </div>

          {allowClear && (
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="w-full mt-3 pt-2.5 border-t border-gray-100 text-center text-xs font-bold text-gray-500 hover:text-[#1E3A8A] transition-colors"
            >
              أي تاريخ
            </button>
          )}
        </div>
      )}
    </div>
  );
};
